/**
 * Notion风格的编辑器工作区组件
 * 左右分屏，中间可拖拽调整宽度
 */
import React, { useState, useCallback, useRef, useImperativeHandle, forwardRef, memo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { MarkdownEditor } from './MarkdownEditor'
import { MarkdownPreview, MarkdownPreviewRef } from '../preview/MarkdownPreview'
import { CodeEditorModal } from '../preview/CodeEditorModal'
import { useAppStore } from '../../store/useAppStore'
import { useDebounce } from '../../hooks/useDebounce'

export interface EditorWorkspaceRef {
  getPreviewElement: () => HTMLDivElement | null
}

/**
 * 从 Markdown 内容中提取标题
 * 提取第一个 # 标题，如果没有则返回 null
 */
const extractTitleFromMarkdown = (content: string): string | null => {
  if (!content) return null

  // 匹配第一个 # 标题（# 后面跟空格和标题内容）
  const titleMatch = content.match(/^#\s+(.+)$/m)
  if (titleMatch && titleMatch[1]) {
    return titleMatch[1].trim()
  }

  return null
}

/**
 * 编辑器工作区组件
 * 采用Notion风格的简洁设计，左右分屏布局
 */
const EditorWorkspaceInner = forwardRef<EditorWorkspaceRef>((_, ref) => {
  const { t } = useTranslation()
  const currentDocument = useAppStore(state => state.currentDocument)
  const saveDocument = useAppStore(state => state.saveDocument)
  const codeEditModal = useAppStore(state => state.codeEditModal)
  const closeCodeEditModal = useAppStore(state => state.closeCodeEditModal)
  const isFullWidth = useAppStore(state => state.isFullWidth)
  
  const [editorWidth, setEditorWidth] = useState(50)
  const [isResizing, setIsResizing] = useState(false)
  const [activeView, setActiveView] = useState<'edit' | 'preview'>('edit')
  const [isMobile, setIsMobile] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<MarkdownPreviewRef>(null)

  // 本地内容状态，用于即时响应输入
  const [localState, setLocalState] = useState({ id: currentDocument?.id, content: currentDocument?.content || '' })
  
  // 防抖处理后的状态
  const debouncedState = useDebounce(localState, 1000)

  // 当切换文档时，更新本地状态
  useEffect(() => {
    if (currentDocument && currentDocument.id !== localState.id) {
      setLocalState({ id: currentDocument.id, content: currentDocument.content })
    }
  }, [currentDocument?.id])

  // 当防抖状态变化时，执行保存
  useEffect(() => {
    // 只有当 ID 匹配且内容确实变化时才保存
    if (currentDocument && 
        debouncedState.id === currentDocument.id && 
        debouncedState.content !== currentDocument.content) {
      const extractedTitle = extractTitleFromMarkdown(debouncedState.content)
      saveDocument(debouncedState.content, extractedTitle || undefined)
    }
  }, [debouncedState, saveDocument, currentDocument?.id])

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    getPreviewElement: () => previewRef.current?.getElement() || null
  }))

  // 处理内容变化
  const handleContentChange = useCallback((newContent: string) => {
    setLocalState(prev => ({ ...prev, content: newContent }))
  }, [])

  // 手动保存处理
  const handleSave = useCallback((content: string) => {
    if (currentDocument) {
      const extractedTitle = extractTitleFromMarkdown(content)
      saveDocument(content, extractedTitle || undefined)
    }
  }, [currentDocument, saveDocument])

  // 处理 Code 保存
  const handleCodeSave = useCallback((newCode: string) => {
    const content = localState.content
    const rawMarkdown = codeEditModal.rawMarkdown
    const language = codeEditModal.language
    
    if (!content || !rawMarkdown) return
  
    const newBlock = `\`\`\`${language}\n${newCode}\n\`\`\``
    
    try {
      // 这里的 rawMarkdown 已经是我们在 markdownRenderer 中精确捕获的原始字符串
      const decodedOld = decodeURIComponent(atob(rawMarkdown))
      
      // 使用精确替换
      const newContent = content.replace(decodedOld, newBlock)
      
      if (newContent !== content) {
        handleContentChange(newContent)
      }
      closeCodeEditModal()
    } catch (e) {
      console.error('Failed to replace code block:', e)
    }
  }, [localState.content, codeEditModal.rawMarkdown, codeEditModal.language, handleContentChange, closeCodeEditModal])

  // 处理分屏器拖拽
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)

    // 添加拖拽状态到body
    document.body.classList.add('dragging')

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()
      const containerWidth = containerRect.width
      
      // 计算像素限制对应的百分比
      // 最小宽度限制为 500px，但不超过容器宽度的 45%
      const minWidthPx = 500
      const minPercent = Math.min((minWidthPx / containerWidth) * 100, 45)
      const maxPercent = 100 - minPercent
      
      const newWidthPercent = ((e.clientX - containerRect.left) / containerWidth) * 100
      
      // 限制宽度在计算出的范围内
      const clampedWidth = Math.max(minPercent, Math.min(maxPercent, newWidthPercent))
      setEditorWidth(clampedWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.classList.remove('dragging')
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [])

  // 如果没有当前文档，显示空状态
  if (!currentDocument) {
    return (
      <div className="h-full flex items-center justify-center">
        <div 
          className="text-center"
          style={{ color: 'var(--text-tertiary)' }}
        >
          <p className="text-lg mb-2">{t('editor.emptyState.title')}</p>
          <p className="text-sm">{t('editor.emptyState.subtitle')}</p>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="flex flex-col md:flex-row h-full overflow-hidden">
      {/* 移动端视图切换按钮 */}
      {isMobile && (
        <div className="flex-shrink-0 flex justify-center p-2 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
          <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            <button
              className={`px-6 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                activeView === 'edit' 
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              onClick={() => setActiveView('edit')}
            >
              {t('editor.edit')}
            </button>
            <button
              className={`px-6 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                activeView === 'preview' 
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              onClick={() => setActiveView('preview')}
            >
              {t('editor.preview')}
            </button>
          </div>
        </div>
      )}

      {/* 左侧编辑器区域 */}
      <div
        style={{
          flex: isMobile ? 'none' : `${editorWidth} 1 0`,
          width: isMobile ? '100%' : 'auto',
          minWidth: isMobile ? 'auto' : '300px',
          background: 'var(--bg-primary)',
          display: isMobile && activeView !== 'edit' ? 'none' : 'flex'
        }}
        className="flex-col h-full"
      >
        {/* 编辑器 */}
        <div className="flex-1 overflow-auto">
          <MarkdownEditor
            value={localState.content}
            onChange={handleContentChange}
            onSave={handleSave}
            placeholder={t('editor.placeholder')}
            className={`px-4 ${isFullWidth ? 'md:px-4' : 'md:px-8'} py-4 md:py-5`}
          />
        </div>
      </div>

      {/* 分割条 */}
      {!isMobile && (
        <div
            className={`
              w-[6px] min-w-[6px] max-w-[6px] h-full 
              cursor-col-resize relative flex-shrink-0 user-select-none
              transition-colors duration-200
              ${isResizing ? '' : ''}
            `}
            style={{
              background: isResizing ? 'var(--border-medium)' : 'var(--border-light)'
            }}
            onMouseDown={handleMouseDown}
          title={t('editor.dragToResize')}
        />
      )}

      {/* 右侧预览区域 */}
      <div
        style={{
          flex: isMobile ? 'none' : `${100 - editorWidth} 1 0`,
          width: isMobile ? '100%' : 'auto',
          minWidth: isMobile ? 'auto' : '300px',
          background: 'var(--bg-primary)',
          display: isMobile && activeView !== 'preview' ? 'none' : 'flex'
        }}
        className="flex flex-col h-full preview-container"
      >
        {/* 预览工作区 */}
        <div className="preview-workspace" style={{ flex: 1, overflow: 'auto' }}>
          {/* 预览面板 - 按照备份文件样式 */}
          <div
            className="preview-pane"
            style={{
              width: '100%',
              minHeight: '100%',
              background: 'var(--bg-primary)'
            }}
          >
            <MarkdownPreview
              ref={previewRef}
              content={localState.content}
              className={`px-4 ${isFullWidth ? 'md:px-4' : 'md:px-8'} py-4 md:py-5`}
            />
          </div>
        </div>
      </div>

      <CodeEditorModal
        isOpen={codeEditModal.isOpen}
        onClose={closeCodeEditModal}
        initialCode={codeEditModal.code}
        language={codeEditModal.language}
        onSave={handleCodeSave}
      />
    </div>
  )
})

EditorWorkspaceInner.displayName = 'EditorWorkspaceInner'

export const EditorWorkspace = memo(EditorWorkspaceInner)

EditorWorkspace.displayName = 'EditorWorkspace'